/** core libraries & third party packages */
import React, { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { MdArrowOutward } from "react-icons/md";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

/** component */

/** services */
import { loginApi, registerApi } from "../../../services/api";

/** custom hooks & query & context */

/** utils */
import { handleSetUserInLocalStorage } from "../../../utils/common";

/** style */
import "./Authentication.scss";

/** interfaces */
import { ILogin, IRegister } from "../../../Interfaces/user";

interface PropsType {
  handleLoginModal: () => void;
  loginModal: boolean;
}
const Authentication: React.FC<PropsType> = (props) => {
  const { handleLoginModal, loginModal } = props;
  const [signUpModal, setSignUpModal] = useState(false);
  const loginModalRef = useRef(null);
  const registerModalRef = useRef(null);
  const [loading, setLoading] = useState(false);

  // query
  const {
    register: loginRegister,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors },
  } = useForm<ILogin>({ mode: "all" });

  const {
    register,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerError },
  } = useForm<IRegister>({ mode: "all" });

  useEffect(() => {
    if (signUpModal || loginModal) {
      document.body.classList.add("modal_open");
    } else {
      document.body.classList.remove("modal_open");
    }
    return () => document.body.classList.remove("modal_open");
  }, [loginModal, signUpModal]);

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      loginModalRef.current !== null &&
      !(loginModalRef.current as HTMLDivElement).contains(event.target as Node)
    ) {
      handleLoginModal();
    }
    if (
      registerModalRef.current !== null &&
      !(registerModalRef.current as HTMLDivElement).contains(
        event.target as Node
      )
    ) {
      setSignUpModal((prev) => !prev);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignUpModal = () => {
    setSignUpModal((prev) => !prev);
    handleLoginModal();
  };

  const handleLogin = async (payload: ILogin) => {
    setLoading(true);
    loginApi(payload)
      .then((res: any) => {
        toast.success("Login successfully");
        handleLoginModal();
        const { user, accessToken } = res.data;
        const localData = {
          ...user,
          accessToken,
        };
        handleSetUserInLocalStorage(localData);
        setLoading(false);
        setTimeout(() => {
          window.location.reload();
        }, 100);
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  const handleRegister = async (payload: IRegister) => {
    const newData = {
      name: payload.firstName.concat(" ", payload.lastName),
      email: payload.email,
      password: payload.password,
    };
    setLoading(true);
    registerApi(newData)
      .then((res: any) => {
        console.log("res", res);
        toast.success("Register successfully");
        handleSignUpModal();
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err.message);
        console.error("error", err);
        setLoading(false);
      });
  };

  return (
    <>
      {loginModal && (
        <div className="login_modal_overlay">
          <div className="login_modal">
            <div className="login_modal_container" ref={loginModalRef}>
              <div className="flex justify-between w-full">
                <div className="text-[25px] font-normal">Log in</div>
                <div
                  className="text-[25px] cursor-pointer"
                  onClick={handleLoginModal}
                >
                  <IoClose size={28} />
                </div>
              </div>
              <form
                className="login_form"
                onSubmit={(event) => {
                  handleLoginSubmit(handleLogin)(event);
                }}
              >
                <div className="input_group">
                  <label htmlFor="email">
                    Email <sup className="text-red-500">*</sup>
                  </label>
                  <input
                    type="text"
                    id="email"
                    placeholder="Enter email address"
                    className="input_field"
                    {...loginRegister("email", {
                      required: {
                        value: true,
                        message: "Email is required",
                      },
                      pattern: {
                        value:
                          /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
                        message: "Invalid email format",
                      },
                    })}
                  />
                  <div className="field_error">
                    {loginErrors.email?.message}
                  </div>
                </div>
                <div className="input_group">
                  <label htmlFor="password">
                    Password <sup className="text-red-500">*</sup>
                  </label>
                  <input
                    type="text"
                    id="password"
                    placeholder="Enter password"
                    className="input_field"
                    {...loginRegister("password", {
                      required: {
                        value: true,
                        message: "Password is required",
                      },
                      minLength: {
                        value: 4,
                        message: "Minimum 4 characters required",
                      },
                    })}
                  />
                  <div className="field_error">
                    {loginErrors.password?.message}
                  </div>
                </div>
                <div>
                  {/* <div className="text-[13px] font-normal cursor-pointer border-b-[1.2px] border-black w-fit text-[var(--text-gray-color)]">
                    Forgot your password?
                  </div> */}
                </div>
                <div className="flex justify-between sm:flex-row flex-col sm:gap-[5px] gap-[10px] items-center mt-4">
                  <button
                    type="submit"
                    className="bg-black button cursor-pointer text-white sm:w-1/2 w-full text-center py-3 text-[14px]  rounded-sm"
                    disabled={loading}
                  >
                    <div className="w-full h-full">
                      {loading ? "Loading..." : "Log in"}
                    </div>
                  </button>
                  <div
                    className="text-[13px]  border-b-[1.2px] border-black  font-semibold duration-300 cursor-pointer hover:text-[var(--danger-text-color)] flex gap-1 items-center"
                    onClick={handleSignUpModal}
                  >
                    New customer? Create your account{" "}
                    <span className="text-[18px]">
                      <MdArrowOutward />
                    </span>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {signUpModal && (
        <div className="login_modal_overlay">
          <div className="login_modal">
            <div className="login_modal_container" ref={registerModalRef}>
              <div className="flex justify-between w-full">
                <div className="text-[25px] font-normal">Register</div>
                <div
                  className="text-[25px] cursor-pointer"
                  onClick={() => setSignUpModal((prev) => !prev)}
                >
                  <IoClose size={28} />
                </div>
              </div>
              <form
                className="login_form"
                onSubmit={(event) => {
                  handleRegisterSubmit(handleRegister)(event);
                }}
              >
                <div className="input_group">
                  <label htmlFor="firstName">
                    First Name <sup className="text-red-500">*</sup>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    placeholder="Enter first name"
                    className="input_field"
                    {...register("firstName", {
                      required: {
                        value: true,
                        message: "FirstName is required",
                      },
                    })}
                  />
                  <div className="field_error">
                    {registerError.firstName?.message}
                  </div>
                </div>
                <div className="input_group">
                  <label htmlFor="lastName">
                    Last Name <sup className="text-red-500">*</sup>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    placeholder="Enter lastName"
                    className="input_field"
                    {...register("lastName", {
                      required: {
                        value: true,
                        message: "LastName is required",
                      },
                    })}
                  />
                  <div className="field_error">
                    {registerError.lastName?.message}
                  </div>
                </div>
                <div className="input_group">
                  <label htmlFor="email">
                    Email <sup className="text-red-500">*</sup>
                  </label>
                  <input
                    type="text"
                    id="email"
                    placeholder="Enter email address"
                    className="input_field"
                    {...register("email", {
                      required: {
                        value: true,
                        message: "Email is required",
                      },
                      pattern: {
                        value:
                          /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
                        message: "Invalid email format",
                      },
                    })}
                  />
                  <div className="field_error">
                    {registerError.email?.message}
                  </div>
                </div>
                <div className="input_group">
                  <label htmlFor="password">
                    Password <sup className="text-red-500">*</sup>
                  </label>
                  <input
                    type="text"
                    id="password"
                    placeholder="Enter password"
                    className="input_field"
                    {...register("password", {
                      required: {
                        value: true,
                        message: "Password is required",
                      },
                      minLength: {
                        value: 4,
                        message: "Minimum 4 characters required",
                      },
                    })}
                  />
                  <div className="field_error">
                    {registerError.password?.message}
                  </div>
                </div>
                <div>
                  {/* <div className="text-[13px] font-normal cursor-pointer border-b-[1.2px] border-black w-fit text-[var(--text-gray-color)]">
                    Forgot your password?
                  </div> */}
                </div>
                <div className="flex justify-between sm:flex-row flex-col sm:gap-[5px] gap-[10px]  items-center mt-4">
                  <button
                    type="submit"
                    className="bg-black button cursor-pointer text-white sm:w-1/2 w-full text-center py-3 text-[14px]  rounded-sm"
                    disabled={loading}
                  >
                    <div className="w-full h-full">
                      {loading ? "Loading..." : "Register"}
                    </div>
                  </button>
                  <div
                    className="text-[13px]  border-b-[1.2px] border-black  font-semibold duration-300 cursor-pointer hover:text-[var(--danger-text-color)] flex gap-1 items-center"
                    onClick={() => {
                      handleLoginModal();
                      setSignUpModal((prev) => !prev);
                    }}
                  >
                    Already have an account? Log in here{" "}
                    <span className="text-[18px]">
                      <MdArrowOutward />
                    </span>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Authentication;
